import React, { useEffect, useState } from "react";
import {
    BarChart,
    Bar,
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
} from "recharts";
import { getPromocodeAnalytics } from "../../api/context/analytics";
import { FiInfo, FiRefreshCw, FiTag, FiUsers, FiGift, FiActivity } from "react-icons/fi";
import Loader from '../../components/loader/loader';
import toast from "react-hot-toast";
import { IoMdArrowBack } from "react-icons/io";
import { useNavigate } from "react-router-dom";

const DEFAULT_LIMIT = 15;

const formatCurrency = (value) => {
    const number = Number(value || 0);
    return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR',
        maximumFractionDigits: 0,
    }).format(number);
};

const formatDate = (value) => {
    if (!value) return "—";
    const d = new Date(value);
    if (isNaN(d.getTime())) return "—";
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const PromoAnalytics = () => {
    const navigate = useNavigate();
    const [analytics, setAnalytics] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);

    const getDefaultDates = () => {
        const endDate = new Date();
        const startDate = new Date();
        startDate.setDate(endDate.getDate() - 7);

        return {
            startDate: startDate.toISOString().split('T')[0],
            endDate: endDate.toISOString().split('T')[0],
        };
    };

    const [filters, setFilters] = useState(getDefaultDates());

    const getTodayDate = () => new Date().toISOString().split('T')[0];

    const summary = analytics?.summary || {};
    const topPromocodes = Array.isArray(analytics?.topPromocodes) ? analytics.topPromocodes : [];
    const promocodes = Array.isArray(analytics?.promocodes) ? analytics.promocodes : [];
    const redemptionTrends = Array.isArray(analytics?.redemptionTrends) ? analytics.redemptionTrends : [];

    const usedPromocodes = topPromocodes.filter(p => Number(p.usageCount) > 0);
    const chartData = usedPromocodes.slice(0, 10);

    const fetchAnalytics = async (isRefresh = false, overrideFilters) => {
        if (isRefresh) setRefreshing(true);
        else setLoading(true);

        const effective = overrideFilters || filters;
        const requestBody = {
            limit: DEFAULT_LIMIT,
            startDate: effective.startDate,
            endDate: effective.endDate,
        };

        try {
            await new Promise((resolve, reject) => {
                getPromocodeAnalytics(requestBody)((response) => {
                    if (response?.statusCode === 200) {
                        setAnalytics(response?.data);

                        const start = new Date(effective.startDate);
                        const end = new Date(effective.endDate);
                        const diffTime = Math.abs(end - start);
                        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;

                        toast.success(`Promo analytics fetched for ${diffDays} ${diffDays === 1 ? 'day' : 'days'}`);
                        resolve(response);
                    } else {
                        setAnalytics(null);
                        reject(new Error('Failed to fetch promocode analytics'));
                    }
                });
            });
        } catch (error) {
            console.error("Error fetching promocode analytics:", error);
            setAnalytics(null);
            toast.error('Failed to fetch promocode analytics data');
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    useEffect(() => {
        fetchAnalytics();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleFilterChange = (key, value) => {
        const today = getTodayDate();

        if (key === 'startDate') {
            if (value > today) return toast.error('Start date cannot be in the future');
            if (filters.endDate && value > filters.endDate) return toast.error('Start date cannot be after end date');
        }

        if (key === 'endDate') {
            if (value > today) return toast.error('End date cannot be in the future');
            if (filters.startDate && value < filters.startDate) return toast.error('End date cannot be before start date');
        }

        setFilters(prev => ({ ...prev, [key]: value }));
    };

    useEffect(() => {
        const hasBothDates = filters.startDate && filters.endDate;
        const isDefaultState = JSON.stringify(filters) === JSON.stringify(getDefaultDates());

        if (hasBothDates && !isDefaultState) {
            const timer = setTimeout(() => fetchAnalytics(true), 500);
            return () => clearTimeout(timer);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters]);

    const handleClearFilters = () => {
        const defaults = getDefaultDates();
        setFilters(defaults);
        setTimeout(() => fetchAnalytics(true, defaults), 100);
    };

    const isFilterActive = () => {
        const defaults = getDefaultDates();
        return filters.startDate !== defaults.startDate || filters.endDate !== defaults.endDate;
    };

    const isLoadingInitial = loading && !refreshing;
    const hasNoData = !loading && !analytics;

    return (
        <div className="min-vh-100 promo-analytics-page">
            <style>{`
                .promo-analytics-page h3 { font-size: 18px !important; }
                .promo-analytics-page h4 { font-size: 18px !important; }
                .promo-analytics-page h5 { font-size: 14px !important; }
                .promo-analytics-page h6 { font-size: 12px !important; }
                .promo-analytics-page p { font-size: 12px; }
                .promo-analytics-page .small,
                .promo-analytics-page small { font-size: 11px !important; }
                .promo-analytics-page .badge { font-size: 10px !important; padding: 3px 7px !important; }
                .promo-analytics-page .card-body { padding: 12px !important; }
                .promo-analytics-page .btn { font-size: 12px; }
                .promo-analytics-page .recharts-text { font-size: 10px !important; }
                .promo-analytics-page .table { font-size: 12px; margin-bottom: 0; }
                .promo-analytics-page .table th { font-size: 10px !important; font-weight: 600; padding: 8px !important; letter-spacing: 0.04em; text-transform: uppercase; color: #64748b; }
                .promo-analytics-page .table td { padding: 8px !important; vertical-align: middle; }
                .promo-analytics-page .row { margin-bottom: 16px !important; margin-top: 0 !important; }
                .promo-analytics-page .mb-2,
                .promo-analytics-page .mb-3,
                .promo-analytics-page .mb-4,
                .promo-analytics-page .my-2,
                .promo-analytics-page .mt-1 { margin-bottom: 16px !important; margin-top: 0 !important; }
                .promo-analytics-page .card { margin-bottom: 0 !important; }

                /* Modern filter row (matches homepage) */
                .promo-analytics-page .filters-row {
                    display: flex !important;
                    flex-wrap: nowrap !important;
                    align-items: center;
                    gap: 8px;
                    overflow-x: auto;
                    -webkit-overflow-scrolling: touch;
                    scrollbar-width: none;
                    padding: 4px 2px 6px;
                }
                .promo-analytics-page .filters-row::-webkit-scrollbar { display: none; }
                .promo-analytics-page .filters-row > * { flex-shrink: 0 !important; }
                .promo-analytics-page .filters-to { white-space: nowrap; color: #94a3b8; padding: 0 2px; font-size: 12px; }
                @media (min-width: 768px) { .promo-analytics-page .filters-row { gap: 12px; } }

                .promo-analytics-page .modern-date {
                    display: inline-block !important;
                    border: 1px solid #e2e8f0 !important;
                    border-radius: 8px !important;
                    padding: 5px 8px !important;
                    width: 135px !important;
                    height: 34px !important;
                    background-color: #fff !important;
                    color: #0f172a !important;
                    margin: 0 !important;
                    font-family: inherit;
                    font-size: 12px !important;
                }
                .promo-analytics-page .modern-date:focus {
                    border-color: #008695 !important;
                    box-shadow: 0 0 0 3px rgba(0,134,149,0.15) !important;
                    outline: none;
                }
                @media (max-width: 576px) {
                    .promo-analytics-page .modern-date { width: 120px !important; padding: 5px 6px !important; }
                }
                .promo-analytics-page .modern-refresh {
                    border: 1px solid #e2e8f0 !important;
                    background: #fff !important;
                    color: #475569 !important;
                    border-radius: 8px !important;
                    padding: 6px 12px !important;
                    font-weight: 500 !important;
                    height: 34px !important;
                    white-space: nowrap;
                    font-size: 12px !important;
                    transition: all 0.15s ease;
                }
                .promo-analytics-page .modern-refresh:hover {
                    border-color: #008695 !important;
                    color: #008695 !important;
                    background: rgba(0,134,149,0.06) !important;
                }
                .promo-analytics-page .filters-clear {
                    height: 34px !important;
                    border-radius: 8px !important;
                    white-space: nowrap;
                    font-size: 12px !important;
                }

                @media (max-width: 767.98px) {
                    .promo-analytics-page .page-header-sticky { flex-wrap: wrap !important; gap: 10px; }
                    .promo-analytics-page .page-header-sticky > * { width: 100%; }
                    .promo-analytics-page .overflow-auto::-webkit-scrollbar { display: none; }
                }
                @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
            `}</style>

            {/* Header — uses shared .page-header-sticky */}
            <div className="page-header-sticky d-flex justify-content-between align-items-center">
                <div className="d-flex align-items-center" style={{ gap: '10px' }}>
                    <div
                        className="back-btn"
                        onClick={() => navigate(-1)}
                        style={{
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '30px',
                            height: '30px',
                            borderRadius: '8px',
                            border: '1px solid #e2e8f0',
                            background: '#fff',
                        }}
                    >
                        <IoMdArrowBack style={{ fontSize: '16px', color: '#475569' }} />
                    </div>
                    <h4 style={{ fontSize: '18px', fontWeight: 700, color: '#111827', margin: 0, letterSpacing: '-0.01em' }}>Promo Analytics</h4>
                </div>

                <div className="filters-row">
                    <input
                        type="date"
                        className="modern-date"
                        value={filters.startDate}
                        onChange={(e) => handleFilterChange('startDate', e.target.value)}
                        max={filters.endDate || getTodayDate()}
                    />
                    <span className="filters-to">to</span>
                    <input
                        type="date"
                        className="modern-date"
                        value={filters.endDate}
                        onChange={(e) => handleFilterChange('endDate', e.target.value)}
                        min={filters.startDate || undefined}
                        max={getTodayDate()}
                    />
                    {isFilterActive() && (
                        <button
                            onClick={handleClearFilters}
                            className="btn btn-sm btn-outline-secondary filters-clear"
                            disabled={refreshing}
                        >
                            Clear
                        </button>
                    )}
                    <button
                        onClick={() => fetchAnalytics(true)}
                        className="btn modern-refresh d-flex align-items-center gap-2"
                        disabled={refreshing}
                    >
                        <FiRefreshCw size={14} style={{ animation: refreshing ? 'spin 1s linear infinite' : 'none' }} />
                        <span>{refreshing ? 'Refreshing' : 'Refresh'}</span>
                    </button>
                </div>
            </div>

            {isLoadingInitial ? (
                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 280 }}>
                    <Loader />
                </div>
            ) : hasNoData ? (
                <div className="d-flex flex-column align-items-center justify-content-center text-center text-muted" style={{ minHeight: 280 }}>
                    <FiInfo size={32} className="mb-3 text-secondary" />
                    <h5 className="fw-bold mb-2">No Data Available</h5>
                    <p className="mb-0" style={{ maxWidth: 420 }}>
                        We couldn't find any promocode analytics for the selected period.
                    </p>
                </div>
            ) : (
              <>
            {/* Summary Cards */}
            <div className="row g-3">
                {[
                    {
                        title: "Total Redemptions",
                        value: summary.totalRedemptions ?? 0,
                        icon: <FiActivity />,
                        color: '#8B5CF6',
                    },
                    {
                        title: "Unique Users",
                        value: summary.totalUniqueUsers ?? 0,
                        icon: <FiUsers />,
                        color: '#6366F1',
                    },
                    {
                        title: "Codes Used",
                        value: `${summary.totalUniqueCodesUsed ?? 0} / ${summary.totalOffers ?? 0}`,
                        icon: <FiTag />,
                        color: '#008695',
                    },
                    {
                        title: "Offers Used In Range",
                        value: summary.offersUsedInRange ?? 0,
                        icon: <FiGift />,
                        color: '#EC4899',
                    },
                ].map((card, i) => (
                    <div key={i} className="col-md-3 col-6">
                        <div style={{
                            background: '#fff',
                            border: '1px solid #e2e8f0',
                            borderRadius: '10px',
                            padding: '14px',
                            height: '100%',
                        }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
                                <div style={{ fontSize: '11px', fontWeight: 500, color: '#64748b' }}>{card.title}</div>
                                <div style={{
                                    width: '30px', height: '30px',
                                    borderRadius: '8px',
                                    background: `${card.color}15`,
                                    color: card.color,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    flexShrink: 0,
                                }}>
                                    {React.cloneElement(card.icon, { size: 14 })}
                                </div>
                            </div>
                            <div style={{ fontSize: '18px', fontWeight: 700, color: '#0f172a', lineHeight: 1.1 }}>{card.value}</div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Top Promocodes - chart + list */}
            <div>
                <h5 className="fw-bold" style={{ margin: '0 0 10px' }}>Top Promocodes by Usage</h5>
                <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '14px', marginBottom: '16px' }}>
                    {chartData.length > 0 ? (
                        <div className="row g-3">
                            <div className="col-md-8" style={{ background: '#f8fafc', borderRadius: '8px' }}>
                                <div style={{ width: "100%", height: 240, padding: 0, marginTop: 12, marginBottom: 8, paddingRight: 12 }}>
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart
                                            data={chartData}
                                            margin={{ top: 8, right: 8, left: 0, bottom: 20 }}
                                            barCategoryGap="40%"
                                        >
                                            <CartesianGrid strokeDasharray="3 3" />
                                            <XAxis
                                                dataKey="code"
                                                angle={-40}
                                                textAnchor="end"
                                                interval={0}
                                                tick={{ fontSize: 10, fontWeight: 600 }}
                                            />
                                            <YAxis tick={{ fontSize: 10, fontWeight: 600 }} />
                                            <Tooltip cursor={{ fill: "transparent" }} />
                                            <Bar
                                                dataKey="usageCount"
                                                name="Redemptions"
                                                fill="#8B5CF6"
                                                radius={[6, 6, 0, 0]}
                                                barSize={32}
                                            />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>

                            <div className="col-md-4">
                                <div style={{
                                    border: '1px solid #e2e8f0',
                                    borderRadius: '8px',
                                    maxHeight: 260,
                                    overflowY: 'auto',
                                    backgroundColor: '#f8fafc',
                                    padding: '10px',
                                }}>
                                    <h6 style={{ fontSize: '11px', fontWeight: 600, color: '#64748b', textAlign: 'center', marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Most Redeemed Codes</h6>
                                    <div className="d-flex flex-column gap-1">
                                        {usedPromocodes.map((promo, index) => (
                                            <div
                                                key={promo.offerId}
                                                className="d-flex justify-content-between align-items-center"
                                                style={{
                                                    backgroundColor: 'white',
                                                    border: '1px solid #eef2f7',
                                                    borderRadius: '6px',
                                                    padding: '6px 8px',
                                                }}
                                            >
                                                <div className="d-flex align-items-center" style={{ gap: '6px', minWidth: 0 }}>
                                                    <span style={{ fontSize: '10px', fontWeight: 700, color: '#94a3b8', minWidth: '18px' }}>
                                                        #{index + 1}
                                                    </span>
                                                    <span
                                                        className="text-truncate"
                                                        style={{ fontSize: '11px', fontWeight: 500, color: '#0f172a', maxWidth: '110px' }}
                                                        title={promo.name || promo.code}
                                                    >
                                                        {promo.code}
                                                    </span>
                                                </div>
                                                <span style={{ fontSize: '12px', fontWeight: 700, color: '#8B5CF6' }}>
                                                    {promo.usageCount}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <p style={{ color: '#94a3b8', margin: 0 }}>No promocodes have been redeemed in this period.</p>
                    )}
                </div>
            </div>

            {/* Redemption Trends */}
            <div>
                <h5 className="fw-bold" style={{ margin: '0 0 10px' }}>Redemption Trends</h5>
                <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '14px', marginBottom: '16px' }}>
                    {redemptionTrends.length > 0 ? (
                        <div style={{ width: "100%", height: 240, background: '#f8fafc', borderRadius: '8px', padding: '10px 0' }}>
                            <ResponsiveContainer>
                                <LineChart data={redemptionTrends} margin={{ top: 20, bottom: 30, right: 20, left: 0 }}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis
                                        dataKey="date"
                                        angle={-40}
                                        textAnchor="end"
                                        tick={{ fontSize: 10, fontWeight: 600 }}
                                        tickFormatter={(v) => new Date(v).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                    />
                                    <YAxis tick={{ fontSize: 10, fontWeight: 600 }} />
                                    <Tooltip
                                        content={({ active, payload, label }) => {
                                            if (active && payload && payload.length) {
                                                return (
                                                    <div style={{
                                                        background: "#fff",
                                                        border: "1px solid #e2e8f0",
                                                        borderRadius: '6px',
                                                        padding: '8px 10px',
                                                        fontSize: "11px",
                                                        lineHeight: "1.4",
                                                        boxShadow: '0 2px 8px rgba(15,23,42,0.06)',
                                                    }}>
                                                        <p style={{ fontWeight: 700, margin: '0 0 4px', fontSize: '11px' }}>{formatDate(label)}</p>
                                                        {payload.map((item, index) => (
                                                            <p key={index} style={{ color: item.stroke, margin: 0, fontSize: '11px' }}>
                                                                {item.name}: <strong>{item.value}</strong>
                                                            </p>
                                                        ))}
                                                    </div>
                                                );
                                            }
                                            return null;
                                        }}
                                    />
                                    <Line type="monotone" dataKey="dailyRedemptions" name="Daily Redemptions" stroke="#8B5CF6" strokeWidth={2} />
                                    <Line type="monotone" dataKey="uniqueUsers" name="Unique Users" stroke="#008695" strokeWidth={2} />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    ) : (
                        <p style={{ color: '#94a3b8', margin: 0 }}>No redemption trend data for this period.</p>
                    )}
                </div>
            </div>

            {/* Promocodes Table */}
            <div>
                <h5 className="fw-bold" style={{ margin: '0 0 10px' }}>All Promocodes</h5>
                <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '14px', marginBottom: '16px', overflow: 'auto' }}>
                    <div className="table-responsive">
                        <table className="table align-middle text-center">
                            <thead style={{ background: '#f8fafc' }}>
                                <tr>
                                    {"Code,Name,Discount,Min Cart,Redemptions,Unique Users,Status,Expires".split(",").map((h, i) => (
                                        <th key={i} className="text-center">{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {promocodes.map((promo) => (
                                    <tr key={promo.offerId} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                        <td className="text-center" style={{ fontWeight: 700, color: '#0f172a', fontSize: '12px' }}>{promo.code}</td>
                                        <td className="text-truncate" style={{ maxWidth: 140, fontSize: '12px' }} title={promo.name}>{promo.name}</td>
                                        <td style={{ fontSize: '12px' }}>
                                            {promo.discountType === 'percentage'
                                                ? `${promo.discountValue}%`
                                                : formatCurrency(promo.discountValue)}
                                        </td>
                                        <td style={{ fontSize: '12px' }}>{formatCurrency(promo.minimumCart)}</td>
                                        <td style={{ fontSize: '12px', fontWeight: 600 }}>{promo.usageCount}</td>
                                        <td style={{ fontSize: '12px' }}>{promo.uniqueUsers}</td>
                                        <td>
                                            <span style={{
                                                background: promo.isActive && promo.approved ? '#dcfce7' : '#f1f5f9',
                                                color: promo.isActive && promo.approved ? '#15803d' : '#64748b',
                                                fontWeight: 600,
                                                fontSize: 10,
                                                padding: '3px 8px',
                                                borderRadius: '999px',
                                                display: 'inline-block',
                                            }}>
                                                {promo.isActive && promo.approved ? 'Active' : 'Inactive'}
                                            </span>
                                        </td>
                                        <td style={{ fontSize: '11px', color: '#94a3b8' }}>{formatDate(promo.expiryDate)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
              </>
            )}
        </div>
    );
};

export default PromoAnalytics;
